import type { CSSProperties } from 'react';
import { Fragment, useMemo } from 'react';

import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

import { Permission } from '@/entities/permissions';
import { useSelectionFunctions } from '@/features/symbol-selector';
import {
  getMaxHeight,
  renderEmptyCells
} from '@/pages/Markets/MarketsComponents/shared/tableHelpers';
import {
  sectionHeaderRowSx,
  sectionNameCellSx,
  shortTitleCellSx,
  tableContainerSx,
  tableHeadRowSx,
  tableSx
} from '@/pages/Markets/MarketsComponents/shared/tableStyles';
import usePermission from '@/shared/hooks/usePermission';
import Checkbox from '@/shared/ui/primitives/Checkbox';

import useCommoditiesSelection from '../useCommoditiesSelection';
import CommoditiesTableRow from './CommoditiesTableRow';

import type { Sx } from '@/types/helpers';
import type { CommoditiesTableSectionType } from '../../types';

const checkboxCellSx: Sx = { pl: 1, pr: 1, width: 24 };

const checkboxSx: Sx = { mb: '3px' };
const checkboxSectionSx: Sx = { mb: '4px' };

const CommoditiesTableTab = ({
  sections,
  height,
  isLoading = false,
  disableSelection = false
}: {
  sections: CommoditiesTableSectionType[];
  height?: number;
  isLoading?: boolean;
  disableSelection?: boolean;
}) => {
  const {
    toggleSectionInSelection,
    allCodesSelected,
    addAllCodesToSelection,
    clearAllCodesFromSelection,
    isSectionSelected
  } = useCommoditiesSelection(sections);

  const handleTabSelection = () => {
    if (allCodesSelected()) {
      clearAllCodesFromSelection();
    } else {
      addAllCodesToSelection();
    }
  };

  const { hasPermission } = usePermission();
  const isPermissionGranted = hasPermission(Permission.ViewCatalogDetails);
  const { isSpotLocked } = useSelectionFunctions();

  const flatRows = useMemo(() => sections.flatMap(s => s.rows), [sections]);

  const shouldRenderLast = useMemo(() => flatRows.some(row => row.last), [flatRows]);
  const shouldRenderLastChange = useMemo(() => flatRows.some(row => row.delta), [flatRows]);

  const shouldRenderDiff = useMemo(() => flatRows.some(row => row.diff), [flatRows]);
  const shouldRenderDiffChange = useMemo(() => flatRows.some(row => row.diffDelta), [flatRows]);

  return (
    <TableContainer
      sx={tableContainerSx}
      style={{ maxHeight: getMaxHeight(height) } as CSSProperties}
    >
      <Table
        stickyHeader
        aria-label='commodities table'
        size='small'
        sx={tableSx}
      >
        <TableHead>
          <TableRow sx={tableHeadRowSx}>
            <TableCell
              align='right'
              component='th'
              scope='row'
              sx={checkboxCellSx}
            >
              {!disableSelection && (
                <Checkbox
                  checked={allCodesSelected()}
                  disabled={!isPermissionGranted}
                  modeLocked={isSpotLocked}
                  onLockedClick={handleTabSelection}
                  sx={checkboxSx}
                  onChange={handleTabSelection}
                />
              )}
            </TableCell>
            <TableCell sx={shortTitleCellSx}>Short Title</TableCell>
            <TableCell align='right'>Date</TableCell>
            {shouldRenderLast && <TableCell align='right'>Last</TableCell>}
            {shouldRenderLastChange && <TableCell align='right'>Change</TableCell>}
            {shouldRenderDiff && <TableCell align='right'>Diff</TableCell>}
            {shouldRenderDiffChange && <TableCell align='right'>Change</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {sections.map(({ name, rows }) => {
            return (
              <Fragment key={name}>
                {name && (
                  <TableRow
                    className='markets-table-row'
                    sx={sectionHeaderRowSx}
                  >
                    <TableCell
                      align='right'
                      component='th'
                      scope='row'
                      sx={checkboxCellSx}
                    >
                      {!disableSelection && (
                        <Checkbox
                          checked={isSectionSelected(rows)}
                          disabled={!isPermissionGranted}
                          modeLocked={isSpotLocked}
                          onLockedClick={() => {
                            toggleSectionInSelection(rows);
                          }}
                          sx={checkboxSectionSx}
                          onChange={() => {
                            toggleSectionInSelection(rows);
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={sectionNameCellSx}>{name}</TableCell>
                    {renderEmptyCells(6)}
                  </TableRow>
                )}
                {rows.map((row, index) => (
                  <CommoditiesTableRow
                    key={row.shortTitle}
                    disableSelection={disableSelection}
                    index={index}
                    isLoading={isLoading}
                    row={row}
                    sections={sections}
                    shouldRenderDiff={shouldRenderDiff}
                    shouldRenderDiffChange={shouldRenderDiffChange}
                    shouldRenderLast={shouldRenderLast}
                    shouldRenderLastChange={shouldRenderLastChange}
                  />
                ))}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CommoditiesTableTab;
